import React, { useState, useRef, useEffect, useCallback } from 'react';
import { accountVerifyEmail } from '../services/api';
import { isNetworkError } from '../services/offlineQueue';
import { readLinkToken, scrubLinkToken } from '../utils/accountLinkRoute';
import { T, S } from '../styles/ds';

// The pathname the emailed confirmation link lands on. `_verify_link()`
// (backend/app/routers/account_auth.py) builds `{PUBLIC_APP_URL}/account/verify-email?token={token}`.
//
// It deliberately lives HERE rather than beside `ACCOUNT_RESET_PASSWORD_PATH` in
// `utils/accountLinkRoute.js`, which is PH-68's file and which this branch carries a BYTE-IDENTICAL
// copy of. PH-68 (pull request #68) merged to `staging` as `1ec6e515` while this screen was being
// written, after this branch had already been cut from the commit before it — so the copy is what
// keeps the two from colliding: an add/add of the same bytes resolves itself silently, an add/add of
// different bytes does not. Verified identical to the merged file, so updating this branch onto
// `staging` drops this copy out of the diff entirely rather than conflicting on an auth file.
//
// Once this branch sits on `staging`, moving this one line next to its sibling is a safe tidy-up.
// Exporting a screen's own constant from the screen is in any case the pattern PH-68 already uses
// for `RESET_LINK_TTL_MINUTES` in `AccountForgotPassword.jsx`.
export const ACCOUNT_VERIFY_EMAIL_PATH = '/account/verify-email';

// How long the emailed confirmation link lives. QUOTED from the backend rather than invented: the
// source of truth is `settings.ACCOUNT_EMAIL_VERIFY_TTL_MINUTES` (backend/app/config.py), which is
// 1440 minutes and which no config endpoint exposes to the frontend today — so this is a deliberate
// duplicate and the two must be changed together. Stated in hours because "24 घंटे" is what a shop
// owner reads; telling them a number the server does not agree with is worse than telling them
// nothing.
const VERIFY_LINK_TTL_HOURS = 24;

// ── What the server can answer, and why three of these read the same ────────────────────────────
//
// `verify_email` (backend/app/routers/account_auth.py:299) answers 400 for every refusal, with one
// of exactly three sentences. The card lists FOUR refusal branches; the backend deliberately gives
// three of them the SAME sentence — "Invalid or expired verification link" — for a bad/expired
// token, for a token minted for a different purpose (wrong `sub` scope), and for a token naming an
// account that no longer exists. That sameness is a security property, not an oversight: telling
// these three apart would let anyone holding a junk token learn whether a given account exists. So
// this screen cannot distinguish them either, and it does not try to.
//
// The classification below reads the error ONLY to pick which screen to show. The backend's own
// English sentence is never rendered — every message on this screen is written here, Hindi-first, in
// plain words. That is the opposite of PH-68's reset screen, which passes the server's sentence
// through: these three sentences are English-only and describe token mechanics, which is not what a
// shop owner standing at a counter needs to read.
const OUTCOME = {
  CONFIRMED: 'confirmed',   // 200 — the address is now confirmed
  ALREADY: 'already',       // 400 code "already_used" — SUCCESS: it was confirmed on an earlier click
  DEAD: 'dead',             // 400 "Invalid or expired…" — bad/expired, wrong scope, or unknown account
  SUPERSEDED: 'superseded', // 400 code "superseded" — the credentials moved on under an older link
  // No separate "network" outcome: a request that never got a response is indistinguishable, on the
  // client, from one that reached the server and was processed but whose reply was lost (timeout,
  // connection reset). Asserting "nothing happened, the link is untouched" for that second case would
  // tell an owner whose address IS already confirmed that it still is not. Both collapse to UNKNOWN.
  UNKNOWN: 'unknown',       // no response, or any other status (5xx) — retryable, we do not know what happened
};

// The PRIMARY signal is `code` — a short machine-readable tag the backend sends alongside the
// sentence (`detail: {"code": "already_used", "msg": "…"}`), which `services/api.js`'s response
// interceptor lifts onto `err.response.data.code` before flattening `detail` down to the plain
// message. It is the one part of the answer that is meant to be read by code, so rewording the
// English — a copy pass, an i18n refactor — cannot change which screen a person sees. Adversarial
// review of PH-69 raised this as HIGH: the sentence alone was carrying a decision that, got wrong,
// tells someone whose address IS confirmed that their link is dead.
//
// The sentence match below is kept as a FALLBACK, not deleted: an older backend, a cached
// deployment, or a proxy that rewrites the body can still produce a 400 with no `code`, and this
// keeps those classifying correctly instead of all collapsing to "dead". Both layers fail in the
// SAFE direction — anything unrecognised is treated as a dead link, never as a success — so the
// worst case is "this link does not work", never telling someone their address is confirmed when it
// is not.
function classify400(err) {
  const code = err?.response?.data?.code;
  if (code === 'already_used') return OUTCOME.ALREADY;
  if (code === 'superseded') return OUTCOME.SUPERSEDED;

  const detail = err?.response?.data?.detail;
  const text = typeof detail === 'string' ? detail.toLowerCase() : '';
  if (text.includes('already been used')) return OUTCOME.ALREADY;
  if (text.includes('no longer valid')) return OUTCOME.SUPERSEDED;
  return OUTCOME.DEAD;
}

// ── Copy ────────────────────────────────────────────────────────────────────────────────────────
// Devanagari Hindi leads, English follows (AGENTS.md, "Copy rules"). Every one of these explains
// what happened and what to do about it, rather than naming a token, a scope or an epoch.

const CHECKING_MSG = 'आपका लिंक जाँचा जा रहा है… / Checking your link…';

const CONFIRMED_TITLE = 'आपका ईमेल पता पक्का हो गया है / Your email address is confirmed';
const CONFIRMED_DETAIL = 'अब आप इसी ईमेल और अपने पासवर्ड से साइन इन कर सकते हैं / You can now sign in with this email and your password';

const ALREADY_TITLE = 'यह ईमेल पता पहले ही पक्का हो चुका है / This email address was already confirmed';
const ALREADY_DETAIL = 'इस लिंक पर पहले भी क्लिक हो चुका है — कुछ और करने की ज़रूरत नहीं। सीधे साइन इन करें / This link was opened once before — nothing more is needed. Just sign in';

const DEAD_TITLE = 'यह लिंक काम नहीं करता / This link does not work';
const DEAD_DETAIL = `लिंक या तो अधूरा/ग़लत है, या उसकी ${VERIFY_LINK_TTL_HOURS} घंटे की समय-सीमा पूरी हो चुकी है / The link is either incomplete or wrong, or its ${VERIFY_LINK_TTL_HOURS}-hour time limit has passed`;

const SUPERSEDED_TITLE = 'यह लिंक अब पुराना हो चुका है / This link is now out of date';
const SUPERSEDED_DETAIL = 'इसी ईमेल से दोबारा साइन अप किया गया था और पासवर्ड बदल गया, इसलिए यह पुराना लिंक अब नहीं चलता / This email was signed up again with a different password, so this older link no longer works';

// The one honest way out of both dead-link states. There is no resend endpoint and no signup screen
// in the app yet, so this screen must NOT offer "ask for a new link" — a button that cannot do
// anything is worse than no button. Recorded as the accepted decision on the PH-69 card, option (c).
const NO_RESEND_DETAIL = 'नया लिंक यहाँ से नहीं भेजा जा सकता — जिन्होंने आपका खाता बनाया था, उनसे सीधे बात करें और नया लिंक भिजवाएँ / A new link cannot be sent from this page — please speak to whoever set up your account and ask them to send a fresh one';

const INCOMPLETE_TITLE = 'लिंक अधूरा है / This link is incomplete';
const INCOMPLETE_DETAIL = 'यह पेज अपने आप नहीं खुलता — ईमेल में आया पूरा लिंक खोलें। अगर आपको अभी-अभी "पक्का हो गया" दिख चुका है और आपने पेज रिफ़्रेश किया है, तो आपका पता पहले ही पक्का हो चुका है — सीधे साइन इन करें / This page does not open on its own — open the full link from your email. If you just saw the confirmation and then refreshed, your address is already confirmed — just sign in';

const UNKNOWN_TITLE = 'अभी जाँच नहीं हो सकी / We could not check the link just now';
const UNKNOWN_DETAIL = 'यह लिंक की गड़बड़ी नहीं है — थोड़ी देर बाद फिर कोशिश करें / This is not a problem with the link — please try again in a little while';

/**
 * PH-69 — the screen the emailed Owner Account confirmation link lands on.
 *
 * Unlike PH-68's reset screen, nothing is typed here: the token is POSTed the moment the page
 * mounts, so the whole screen is one of five answers to one request. Two consequences shape the code
 * below, and both are the reason this is a HIGH blast-radius change:
 *
 *  1. The request fires from an EFFECT, and `index.js` renders under `React.StrictMode`, which
 *     double-invokes effects on mount. A second POST with the same token hits the blocklist and
 *     answers 400 "already been used" — which, unguarded, would turn a perfectly good first-time
 *     confirmation into a visible error. `firedRef` below is what makes it exactly once.
 *  2. Confirming issues NO session. The endpoint sets no cookie and returns nothing to store, and
 *     this component writes nothing to localStorage. The way forward is the ordinary Owner Account
 *     sign-in screen, via `onSignIn`.
 *
 * `onSignIn` is the single way forward from EVERY terminal state here — success, already-confirmed,
 * both dead-link states, the retryable-error state, and the missing-token state — so no state on
 * this screen is a dead end, even where the link itself is.
 */
function AccountVerifyEmail({ onSignIn }) {
  // Captured ONCE, in a pure initializer with no side effect of any kind — the URL scrub lives in
  // the effect below. StrictMode double-invokes the render body, so a scrub here would let the
  // second pass read an already-stripped URL and capture an empty token, reporting "this link is
  // incomplete" for a link that was perfectly good. `SetupWizard.jsx:13-22` has exactly that side
  // effect inside its initializer; this mirrors its intent, not its shape.
  const [token] = useState(() => readLinkToken());

  // Whitespace-only counts as absent: it can never be a real token, and sending it would spend a
  // request that is certain to 400.
  const hasToken = !!token.trim();

  const [outcome, setOutcome] = useState(null);
  const [loading, setLoading] = useState(hasToken);

  // Set true on mount and false ONLY in the cleanup — PH-62's regression was a ref that only ever
  // went false, which StrictMode's double-invoke (setup, cleanup, setup again) then left stuck
  // false. Declared before the confirm effect so it is true again by the time a response lands.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Take the token out of the address bar, so it never reaches browser history, a bookmark, a URL
  // pasted to someone else, or a screenshot of the address bar (AGENTS.md §6). In an EFFECT, never
  // the initializer above; `replaceState` is idempotent, so StrictMode running it twice is harmless.
  //
  // The token survives in `token` above, captured before this ran — which is what makes the retry
  // button work after a connection failure even though the address bar is already clean.
  useEffect(() => { scrubLinkToken(); }, []);

  // "Exactly once" for the MOUNT request. This is the StrictMode risk that PH-68 did not have: its
  // request fired from a form submit, ours fires from an effect, and StrictMode invokes effects
  // twice on mount. A ref is the only guard that works — a state flag is not readable by the second
  // invoke, which runs before any re-render. It only ever goes true, deliberately: this must fire
  // once per page load, not once per effect setup.
  const firedRef = useRef(false);

  // Separate from `firedRef`: this one guards the RETRY button against a double click, where two
  // clicks in the same tick would both read the pre-update `loading` value.
  const inFlightRef = useRef(false);

  const runConfirm = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    setOutcome(null);
    try {
      await accountVerifyEmail(token);
      if (!mountedRef.current) return;
      // Nothing is written to localStorage, and there is nothing to write: confirming issues no
      // session (AGENTS.md §6 / this ticket's acceptance criteria). Signing in is a separate act.
      setOutcome(OUTCOME.CONFIRMED);
    } catch (err) {
      if (!mountedRef.current) return;
      const status = err?.response?.status;
      if (isNetworkError(err)) {
        // Codex review finding on PR #70 (real, confirmed): `isNetworkError()` also matches a
        // timeout or connection reset that happened AFTER the server already committed the
        // verification — the client cannot tell "never arrived" apart from "arrived, but the
        // reply never made it back". Asserting NETWORK here would tell an owner whose address IS
        // now confirmed that it is still unconfirmed and the link is still usable, which the retry
        // button would then prove wrong with a same-token 400. UNKNOWN is the honest state: "we
        // don't know, please retry" is safe in both directions, exactly like the non-network branch
        // below already treats an unrecognised status.
        setOutcome(OUTCOME.UNKNOWN);
      } else if (status === 400) {
        setOutcome(classify400(err));
      } else {
        // Deliberately no 429 branch: `verify_email` calls neither `check_ip_throttle` nor
        // `record_ip_failure`, so handling 429 here would be handling a status it cannot return.
        setOutcome(OUTCOME.UNKNOWN);
      }
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!hasToken) return;      // nothing is sent; the missing-token state below says what to do
    if (firedRef.current) return;
    firedRef.current = true;
    runConfirm();
  }, [hasToken, runConfirm]);

  // Same page-wrapper shape as AccountSignIn.jsx and AccountResetPassword.jsx — design-system
  // tokens only, no hand-rolled colour or font size.
  const wrapStyle = { minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: T.font };
  const successBox = { padding: '14px 16px', background: T.green.bg, border: `1px solid ${T.green.border}`, borderRadius: T.rs, fontFamily: T.font, textAlign: 'center' };

  const signInButton = (
    <button
      type="button" onClick={onSignIn}
      style={{ ...S.btnPri, width: '100%', marginTop: 12 }}
    >
      साइन इन करें / Sign in →
    </button>
  );

  // No disabled/in-flight variant here on purpose: the moment this is pressed the screen switches to
  // the loading panel below, so this button is never on screen while a request is out. `inFlightRef`
  // in `runConfirm` is what actually refuses a second press in the same tick.
  const retryButton = (
    <button
      type="button" onClick={runConfirm}
      style={{ ...S.btnPri, width: '100%', marginTop: 12 }}
    >
      फिर कोशिश करें / Try again
    </button>
  );

  const successPanel = (title, detail) => (
    <div>
      <div role="status" style={successBox}>
        <div style={S.successEmoji} aria-hidden="true">✅</div>
        <div style={S.successTitle}>{title}</div>
        <div style={S.successDetail}>{detail}</div>
      </div>
      {signInButton}
    </div>
  );

  // `extra` carries the "a new link cannot be sent from here" line on the two dead-link states.
  const alertPanel = (title, detail, extra, action) => (
    <div>
      <div role="alert" style={S.alertBox}>
        <div style={S.alertTitle}>{title}</div>
        <div style={{ ...S.alertDetail, marginBottom: extra ? 10 : 0 }}>{detail}</div>
        {extra && <div style={{ ...S.alertDetail, marginBottom: 0 }}>{extra}</div>}
      </div>
      {action}
    </div>
  );

  // True on the one retryable state, where the primary button is "Try again" rather than sign-in.
  // Everywhere else the primary button IS sign-in, so repeating it underneath would be two controls
  // doing one thing — which is what PH-68's own success state avoids.
  const needsSecondWayOut = !loading && outcome === OUTCOME.UNKNOWN;

  let body;
  if (loading) {
    body = (
      <div role="status" style={S.screenStatus}>
        {CHECKING_MSG}
      </div>
    );
  } else if (outcome === OUTCOME.CONFIRMED) {
    body = successPanel(CONFIRMED_TITLE, CONFIRMED_DETAIL);
  } else if (outcome === OUTCOME.ALREADY) {
    // The card's rule, and it is the point of this screen: for a CONFIRMATION link, already-used is
    // a good outcome, not a failure. The person clicked their link twice, or the mail client
    // prefetched it. Their address is confirmed either way, so this is green and it offers sign-in.
    body = successPanel(ALREADY_TITLE, ALREADY_DETAIL);
  } else if (outcome === OUTCOME.SUPERSEDED) {
    body = alertPanel(SUPERSEDED_TITLE, SUPERSEDED_DETAIL, NO_RESEND_DETAIL, signInButton);
  } else if (outcome === OUTCOME.DEAD) {
    body = alertPanel(DEAD_TITLE, DEAD_DETAIL, NO_RESEND_DETAIL, signInButton);
  } else if (outcome === OUTCOME.UNKNOWN) {
    body = alertPanel(UNKNOWN_TITLE, UNKNOWN_DETAIL, null, retryButton);
  } else {
    // No token: a refresh lands here by design, because the effect above scrubbed the address bar.
    body = alertPanel(INCOMPLETE_TITLE, INCOMPLETE_DETAIL, null, signInButton);
  }

  return (
    <div style={wrapStyle}>
      <div style={{ marginBottom: 32, textAlign: 'center' }}>
        <div style={S.screenTitle}>ईमेल पता पक्का करें</div>
        <div style={S.screenSubtitle}>Confirm your email address</div>
      </div>

      <div style={{ ...S.cardPad, width: '100%', maxWidth: 360 }}>
        {body}

        {needsSecondWayOut && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
            <button
              type="button" onClick={onSignIn}
              style={{ ...S.btnSec, background: 'none', border: 'none', color: T.mute, fontSize: 12, cursor: 'pointer' }}
            >
              ← साइन इन पर जाएँ / Go to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AccountVerifyEmail;
